import React, { useState, useEffect, useCallback } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import PauseIcon from '@material-ui/icons/Pause'
import { makeStyles } from '@material-ui/core/styles'
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined'

const useStyles = makeStyles((theme) => ({
  audioPlayerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    margin: '15px 0px',
  },
  audioPlayer: {
    display: 'flex',
    width: '100%',
    height: 48,
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 25,
  },
  control: {
    color: '#fff',
    width: 28,
    height: 28,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    cursor: 'pointer',
    overflow: 'hidden',
    border: '1px solid #00B2AD',
    '& div': {
      width: '100%',
      height: '100%',
      fontSize: 14,
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center',
    },
  },
  timeConut: {
    fontSize: 12,
    color: theme.colors.primary,
    marginLeft: '.75rem',
    fontWeight: 600,
  },
  progressBar: {
    position: 'relative',
    flexGrow: 1,
    margin: '0 8px',
    height: 1,
    borderRadius: 5,
    backgroundColor: '#F3F3F3',
  },
  progressBarBounce: {
    width: 9,
    height: 9,
    backgroundColor: '#00B2AD',
    borderRadius: '50%',
    position: 'absolute',
    bottom: -5,
    cursor: 'pointer',
    left: 0,
    zIndex: 9,
  },
  progressBarLine: {
    height: 2,
    width: 0,
    backgroundColor: '#00B2AD',
    position: 'absolute',
    left: 0,
  },
  playerInfo: {
    marginTop: 8,
    color: '#404040',
  },
}))

const timerFormat = 'm:ss'

const initAudioState = {
  currentMusic: null,
  totalDuration: 0,
  currentTime: '0:00',
  duration: '0:00',
  position: 0,
  playerRef: null,
}

let isChangePosition = false
function setIsChangePosition(bool) {
  isChangePosition = bool
}

const Audios = (props) => {
  const { audioUrl, isLoadingAudio, isStopAudio, currentMusicPlaying } = props
  const classes = useStyles()

  const [isAudioLoad, setIsAudioLoad] = useState(false)
  const [isPlayState, setIsPlayState] = useState(false)
  const [audioState, setAudioState] = useState(initAudioState)

  const onLoadedData = (currentMusic) => {
    const duration =
      '-' + moment.utc(currentMusic.duration * 1000).format(timerFormat)
    setIsAudioLoad(true)
    setAudioState((prev) => ({
      ...prev,
      currentMusic,
      totalDuration: currentMusic.duration,
      currentTime: '0:00',
      duration,
    }))

    currentMusic.onended = function () {
      setIsPlayState(false)
    }
  }

  const emptyPlayerList = useCallback(() => {
    if (currentMusicPlaying.length > 0) {
      currentMusicPlaying.forEach((item, key) => {
        item.audio && item.audio.pause()
        item.setIsPlayState && item.setIsPlayState(false)
        currentMusicPlaying.splice(key, 1)
      })
    }
  }, [currentMusicPlaying])

  const handlePlay = () => {
    setIsChangePosition(false)
    emptyPlayerList()
    let currentMusic = audioState.currentMusic
    currentMusicPlaying.push({
      audio: currentMusic,
      setIsPlayState,
    })

    if (currentMusic) {
      currentMusic.addEventListener('timeupdate', () =>
        moveProgressByDuration(currentMusic),
      )
      currentMusic.play()
    }
    setIsPlayState(true)
  }

  const handlePause = () => {
    let currentMusic = audioState.currentMusic
    setIsPlayState(false)
    currentMusic && currentMusic.pause()
  }

  const handleOnMouseMove = (e) => {
    if (isChangePosition) {
      moveProgressByPosition(e)
    }
  }

  const handleOnMouseUp = () => {
    setIsChangePosition(false)
  }

  const handleProgressOnMouseDown = () => {
    setIsChangePosition(true)
  }

  const moveProgressByPosition = (e) => {
    let elemData = audioState.playerRef.getBoundingClientRect()
    let newPosition = e.clientX - elemData.left
    if (newPosition >= 0 && newPosition <= elemData.width) {
      let position = (100 * newPosition) / elemData.width
      if (position >= 99) {
        position = 99
      }

      let newTime = (audioState.totalDuration * position) / 100
      let currentTime = moment.utc(newTime * 1000).format(timerFormat)
      let duration =
        '-' +
        moment
          .utc((audioState.totalDuration - newTime) * 1000)
          .format(timerFormat)
      audioState.currentMusic && (audioState.currentMusic.currentTime = newTime)

      setAudioState((prev) => ({
        ...prev,
        currentTime: currentTime,
        duration: duration,
        position: position,
      }))
    }
  }

  const moveProgressByDuration = (currentMusic) => {
    if (!isChangePosition) {
      let currentTime = moment
        .utc(currentMusic.currentTime * 1000)
        .format(timerFormat)
      let duration =
        '-' +
        moment
          .utc((audioState.totalDuration - currentMusic.currentTime) * 1000)
          .format(timerFormat)

      let position = (currentMusic.currentTime / audioState.totalDuration) * 100
      if (position >= 99) {
        position = 99
      }

      setAudioState((prev) => ({
        ...prev,
        currentTime,
        duration,
        position,
      }))
    }
  }

  const setRef = (ref) => {
    if (ref && !audioState.playerRef) {
      setAudioState((prev) => ({
        ...prev,
        playerRef: ref,
      }))
    }
  }

  useEffect(() => {
    let isMounted = true
    if ((isLoadingAudio || isStopAudio) && isMounted) {
      let currentMusic = audioState.currentMusic
      if (currentMusic) {
        currentMusic.pause()
        currentMusic.currentTime = 0 //
      }
      setIsPlayState(false)
      emptyPlayerList() //
    }
    return () => {
      isMounted = false
    }
  }, [isStopAudio, isLoadingAudio, audioState.currentMusic, emptyPlayerList])

  useEffect(() => {
    let isMounted = true
    setAudioState(initAudioState)
    if (!isLoadingAudio && audioUrl && isMounted) {
      let currentMusic = new Audio(audioUrl)
      currentMusic.addEventListener('loadeddata', () => {
        onLoadedData(currentMusic)
      })
    }
    return () => {
      isMounted = false
    }
  }, [audioUrl, isLoadingAudio])

  useEffect(() => {
    return () => {
      emptyPlayerList() //
    }
  }, [emptyPlayerList])

  return (
    <>
      {isLoadingAudio ? null : (
        <div className={classes.audioPlayerWrapper}>
          {isAudioLoad && !isLoadingAudio && (
            <div
              className={classes.audioPlayer}
              onMouseUp={handleOnMouseUp}
              onMouseMove={handleOnMouseMove}
              onMouseLeave={handleOnMouseUp}
            >
              <div className={classes.control}>
                {isPlayState ? (
                  <div onClick={handlePause}>
                    <PauseIcon
                      fontSize="small"
                      style={{
                        color: '#00B2AD',
                      }}
                    />
                  </div>
                ) : (
                  <div onClick={handlePlay}>
                    <PlayArrowOutlinedIcon
                      fontSize="small"
                      style={{
                        color: '#00B2AD',
                      }}
                    />
                  </div>
                )}
              </div>

              <div className={classes.timeConut}>{audioState.currentTime}</div>

              <div
                ref={setRef}
                className={classes.progressBar}
                onClick={moveProgressByPosition}
              >
                <div
                  className={classes.progressBarBounce}
                  style={{ left: audioState.position + '%' }}
                  onMouseDown={handleProgressOnMouseDown}
                />
                <div
                  className={classes.progressBarLine}
                  style={{ width: audioState.position + '%' }}
                />
              </div>

              <div className={classes.timeConut}>{audioState.duration}</div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

Audios.propTypes = {
  audioUrl: PropTypes.string,
  isStopAudio: PropTypes.bool,
  isLoadingAudio: PropTypes.bool,
  currentMusicPlaying: PropTypes.array,
}

export default Audios
